import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import contentService, { GalleryItem } from '../services/contentService';
import { getImageUrl } from '../services/api';
import { translateWithCache } from '../services/translation';

const GalleryPage: React.FC = () => {
  const { i18n } = useTranslation();
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        const [items, cats] = await Promise.all([
          contentService.getGalleryItems(),
          contentService.getGalleryCategories(),
        ]);

        const lang = (i18n.language || 'es').split('-')[0];

        let translatedItems = items;
        let translatedCategories = cats;

        if (lang === 'en') {
          // Translate gallery items
          translatedItems = await Promise.all(
            items.map(async (item) => ({
              ...item,
              title: item.title ? await translateWithCache(item.title, 'es', 'en') : item.title,
              description: item.description ? await translateWithCache(item.description, 'es', 'en') : item.description,
            }))
          );

          // Translate category names (they're just strings)
          translatedCategories = await Promise.all(
            cats.map(async (cat) => await translateWithCache(cat, 'es', 'en'))
          );
        }

        setGalleryItems(translatedItems);
        setCategories(translatedCategories);
      } catch (err: any) {
        console.error('Error fetching gallery data:', err);
        setError('Failed to load gallery');
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryData();
  }, [i18n.language]);

  const handleCategoryChange = async (event: SelectChangeEvent) => {
    const category = event.target.value;
    setSelectedCategory(category);
    setLoading(true);

    try {
      const items = await contentService.getGalleryItems(category || undefined);
      const lang = (i18n.language || 'es').split('-')[0];

      let translatedItems = items;

      if (lang === 'en') {
        // Translate gallery items
        translatedItems = await Promise.all(
          items.map(async (item) => ({
            ...item,
            title: item.title ? await translateWithCache(item.title, 'es', 'en') : item.title,
            description: item.description ? await translateWithCache(item.description, 'es', 'en') : item.description,
          }))
        );
      }

      setGalleryItems(translatedItems);
    } catch (err: any) {
      console.error('Error fetching gallery items:', err);
      setError('Failed to load gallery items');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: '400px',
          backgroundImage: 'url(/images/bg-burger-elements.svg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography
              variant="h1"
              gutterBottom
              sx={{
                fontFamily: '"Bebas Neue", sans-serif',
                fontWeight: 400,
                mb: 2,
              }}
            >
              Galería
            </Typography>
            <Typography
              variant="h5"
              sx={{
                opacity: 0.9,
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.4,
              }}
            >
              Explora nuestros momentos más especiales y las creaciones que nos hacen únicos.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Gallery Content */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          {/* Category Filter */}
          {categories.length > 0 && (
            <Box sx={{ mb: 6, display: 'flex', justifyContent: 'center' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filtrar por Categoría</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Filtrar por Categoría"
                  onChange={handleCategoryChange}
                >
                  <MenuItem value="">
                    <em>Todas las Categorías</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Gallery Grid */}
          {galleryItems.length === 0 ? (
            <Box textAlign="center" sx={{ py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No se encontraron elementos en la galería.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {galleryItems.map((item, index) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  lg={3} 
                  key={item.id}
                  sx={{
                    // Create Instagram-style masonry layout
                    '&:nth-of-type(4n+1)': {
                      '& .MuiCard-root': {
                        height: '300px',
                      },
                    },
                    '&:nth-of-type(4n+2)': {
                      '& .MuiCard-root': {
                        height: '250px',
                      },
                    },
                    '&:nth-of-type(4n+3)': {
                      '& .MuiCard-root': {
                        height: '350px',
                      },
                    },
                    '&:nth-of-type(4n+4)': {
                      '& .MuiCard-root': {
                        height: '280px',
                      },
                    },
                  }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="100%"
                      image={getImageUrl(item.image_url)}
                      alt={item.title}
                      sx={{ 
                        objectFit: 'cover',
                        height: '100%',
                      }}
                    />
                    {item.title && (
                      <CardContent
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                          color: 'white',
                          p: 2,
                        }}
                      >
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          {item.title}
                        </Typography>
                        {item.description && (
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {item.description}
                          </Typography>
                        )}
                      </CardContent>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default GalleryPage; 